import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import {
  getOnlineClassImageUrl,
  getOnlineClassVideoUrl,
  onlineClassApi,
} from "../../api/onlineClassApi";
import { buildAppPath, isAdminRole, isProviderRole } from "../../utils/auth";
import "../../components/MasterCrud.css";

export default function OnlineClassDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const showAttendance = String(window.location.pathname).includes("/attendance");
  const canManage = isAdminRole() || isProviderRole();

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await onlineClassApi.getOne(id);
      setItem(data);
      if (showAttendance && canManage) {
        const rows = await onlineClassApi.attendance(id);
        setAttendance(rows);
      }
    } catch (err) {
      showToast(err.message || "Failed to load class", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, showAttendance]);

  const run = async (action) => {
    setBusy(true);
    try {
      if (action === "start") {
        await onlineClassApi.start(id);
        showToast("Class started", "success");
      }
      if (action === "end") {
        if (!window.confirm("Are you sure you want to end this live class?")) {
          return;
        }
        await onlineClassApi.end(id);
        showToast("Class ended", "success");
      }
      if (action === "cancel") {
        if (!window.confirm("Cancel this class?")) return;
        await onlineClassApi.cancel(id);
        showToast("Class cancelled", "success");
      }
      await load();
    } catch (err) {
      showToast(err.message || "Action failed", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="master-page">
        <div className="master-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="master-page">
        <div className="master-card">
          <p>Class not found</p>
          <Link to={buildAppPath("online-classes")}>← Back to Online Classes</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="master-page">
      <div className="master-card">
        <div className="master-card__header">
          {/* <p style={{ marginBottom: 8 }}>
            <Link to={buildAppPath("online-classes")}>← Back to Online Classes</Link>
          </p> */}
          <h2>{item.title}</h2>
          <p>Class details, schedule, and live session controls.</p>
        </div>

        <div className="master-form__grid" style={{ marginTop: 18 }}>
          <div className="master-form__field">
            {item.thumbnail ? (
              <img
                src={getOnlineClassImageUrl(item.thumbnail)}
                alt=""
                style={{
                  width: "100%",
                  maxWidth: 280,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 12,
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  maxWidth: 280,
                  height: 200,
                  background: "#f5f5f5",
                  borderRadius: 12,
                }}
              />
            )}
            {item.video || item.hasVideo ? (
              <div style={{ marginTop: 14, maxWidth: 420 }}>
                <p style={{ marginBottom: 8 }}>
                  <strong>Class Video</strong> (play only — download disabled)
                </p>
                <video
                  src={getOnlineClassVideoUrl(item.video)}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  style={{ width: "100%", borderRadius: 12, background: "#000" }}
                >
                  Your browser does not support video playback.
                </video>
              </div>
            ) : null}
          </div>

          <div className="master-form__field">
            <p><strong>Status:</strong> {String(item.status).toUpperCase()}</p>
            <p><strong>Astrologer:</strong> {item.astrologer?.name || "—"}</p>
            <p>
              <strong>Start:</strong>{" "}
              {item.scheduledStart
                ? new Date(item.scheduledStart).toLocaleString()
                : "—"}
            </p>
            <p>
              <strong>End:</strong>{" "}
              {item.scheduledEnd
                ? new Date(item.scheduledEnd).toLocaleString()
                : "—"}
            </p>
            <p>
              <strong>Price:</strong>{" "}
              {item.isPaid ? `₹${item.price}` : "Free"}
            </p>
            <p>
              <strong>Enrollment required:</strong>{" "}
              {item.isEnrollmentRequired ? "Yes" : "No"}
            </p>
            <p style={{ whiteSpace: "pre-wrap" }}>{item.description}</p>
          </div>
        </div>

        {canManage ? (
          <div className="master-form__actions" style={{ marginTop: 18 }}>
            {item.status === "scheduled" ? (
              <>
                <button
                  type="button"
                  className="master-btn master-btn--edit"
                  disabled={busy}
                  onClick={() =>
                    navigate(buildAppPath(`online-classes/${id}/edit`))
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="master-btn master-btn--primary"
                  disabled={busy}
                  onClick={() => run("start")}
                >
                  Start Class
                </button>
                <button
                  type="button"
                  className="master-btn master-btn--delete"
                  disabled={busy}
                  onClick={() => run("cancel")}
                >
                  Cancel
                </button>
              </>
            ) : null}
            {item.status === "live" ? (
              <button
                type="button"
                className="master-btn master-btn--delete"
                disabled={busy}
                onClick={() => run("end")}
              >
                End Class
              </button>
            ) : null}
            <button
              type="button"
              className="master-btn master-btn--edit"
              onClick={() =>
                navigate(buildAppPath(`online-classes/${id}/attendance`))
              }
            >
              Attendance
            </button>
          </div>
        ) : null}
      </div>

      {showAttendance ? (
        <div className="master-card">
          <div className="master-card__header">
            <h3>Attendance</h3>
            <p>
              {attendance?.length
                ? `${attendance.length} attendance record${
                    attendance.length === 1 ? "" : "s"
                  }`
                : "No attendance records yet."}
            </p>
          </div>
          <div className="master-table-wrap">
            <table className="master-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Joined</th>
                  <th>Left</th>
                  <th>Duration (s)</th>
                </tr>
              </thead>
              <tbody>
                {!attendance?.length ? (
                  <tr>
                    <td colSpan={5} className="master-table__empty">
                      No attendance records yet.
                    </td>
                  </tr>
                ) : (
                  attendance.map((row, index) => (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>{row.user?.name || row.userId}</td>
                      <td>
                        {row.joinedAt
                          ? new Date(row.joinedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        {row.leftAt
                          ? new Date(row.leftAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>{row.durationSeconds ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
